import { createFileRoute, Navigate } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { orgRoleApi, roleApi } from "@/lib/api/services/role.api";
import { useLingui } from "@lingui/react/macro";
import { ShieldCheck, ChevronDown, ChevronUp, RefreshCcw } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { ListSectionHeader } from "@/components/common/ListSectionHeader";
import { StatCard } from "@/components/common/StatCard";
import {
  PermissionMatrix,
  RoleDialog,
  RolesTable,
  RoleSheet,
  ScopeAssignmentsTable,
} from "@/components/permissions";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import type { Role } from "@/lib/api/admin-api";

export const Route = createFileRoute("/(protected)/_layout/_org/roles")({
  component: OrgRolesPage,
});

function OrgRolesPage() {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const { isAuthenticated, isLoading: authLoading, hasPermission } = useAuth();
  const [matrixExpanded, setMatrixExpanded] = useState(false);
  const [scopesExpanded, setScopesExpanded] = useState(true);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [selectedSystemRole, setSelectedSystemRole] = useState<Role | null>(null);

  const canRead = hasPermission("role:read");
  const canWrite = hasPermission("role:write");

  const {
    data: orgRoles = [],
    isLoading: rolesLoading,
    isFetching: rolesFetching,
  } = useQuery({
    queryKey: ["org-roles"],
    queryFn: () => orgRoleApi.getAll(),
    enabled: isAuthenticated && canRead,
  });

  const { data: permissionGroups = [] } = useQuery({
    queryKey: ["roles", "permissions"],
    queryFn: () => roleApi.getPermissions(),
    enabled: isAuthenticated && canRead,
  });

  const { data: scopeAssignments = [] } = useQuery({
    queryKey: ["org-roles", "scopes"],
    queryFn: () => orgRoleApi.getScopes(),
    enabled: isAuthenticated && canRead,
  });

  if (authLoading) return <OrgRolesSkeleton />;

  if (!canRead) {
    return (
      <Navigate
        to="/unauthorized"
        search={{
          reason: "permission_required",
          from: "/roles",
          requiredPermission: "role:read",
        }}
        replace
      />
    );
  }

  // orgRoleApi.getAll() returns both org-custom and system roles so that role
  // dropdowns elsewhere have the full list. Split them here for separate display.
  const customRoles = orgRoles.filter((r) => !r.isSystem);
  const systemRoles = orgRoles.filter((r) => r.isSystem);

  if (rolesLoading && customRoles.length === 0) return <OrgRolesSkeleton />;

  const allRolesForMatrix = orgRoles;

  return (
    <div className="flex flex-col gap-5">
      {/* Header */}
      <ListSectionHeader title={t`Roles`} count={customRoles.length}>
        <Button
          variant="ghost"
          size="sm"
          onClick={() =>
            queryClient.invalidateQueries({ queryKey: ["org-roles"] })
          }
        >
          <RefreshCcw className={rolesFetching ? "animate-spin" : ""} />
          {t`Refresh`}
        </Button>
        {canWrite && (
          <RoleDialog
            permissionGroups={permissionGroups}
            onClose={() =>
              queryClient.invalidateQueries({ queryKey: ["org-roles"] })
            }
            createFn={orgRoleApi.create}
          />
        )}
      </ListSectionHeader>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 p-3 bg-(--color-surface-muted) rounded-lg">
        <StatCard
          label={t`Custom Roles`}
          value={customRoles.length}
          icon={ShieldCheck}
        />
        <StatCard
          label={t`Scope Assignments`}
          value={scopeAssignments.length}
          icon={ShieldCheck}
        />
      </div>

      {/* Custom roles table */}
      <RolesTable
        roles={customRoles}
        onSelect={setSelectedRole}
        columns={["name", "code", "permissions", "actions"]}
        emptyMessage={t`No custom roles yet. Create one to get started.`}
        className="rounded-lg border overflow-x-auto"
      />

      {/* Default roles (read-only reference) */}
      {systemRoles.length > 0 && (
        <div className="rounded-lg border">
          <div className="px-4 py-3 bg-(--color-surface-muted) border-b text-sm font-medium flex items-center justify-between">
            <span>{t`Default Roles`}</span>
            <span className="text-xs text-(--color-text-secondary) font-normal">{t`Read-only — managed by Yes, Chef!`}</span>
          </div>
          {/* Deliberately headerless — the strip above is this list's header, so
              it uses the `ui/table` primitives rather than `RolesTable`. */}
          <Table>
            <TableBody>
              {systemRoles.map((role) => (
                <TableRow
                  key={role.id}
                  className="last:border-b-0 opacity-60 hover:opacity-100 cursor-pointer transition-opacity"
                  onClick={() => setSelectedSystemRole(role)}
                >
                  <TableCell className="px-4 py-3 font-medium">{role.name}</TableCell>
                  <TableCell className="px-4 py-3 font-mono text-xs text-(--color-text-secondary)">
                    {role.code}
                  </TableCell>
                  <TableCell className="px-4 py-3 text-center">
                    <Badge variant="neutral">
                      {(role.permissions as string[]).join("") === "*:*"
                        ? t`All`
                        : (role.permissions as string[]).length}
                    </Badge>
                  </TableCell>
                  <TableCell className="px-4 py-3 text-right">
                    <span className="text-xs text-(--color-text-secondary) px-2">{t`Default`}</span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Permission matrix */}
      <div className="rounded-lg border">
        <button
          type="button"
          onClick={() => setMatrixExpanded((v) => !v)}
          className="w-full flex items-center justify-between px-4 py-3 bg-(--color-surface-muted) border-b text-sm font-medium hover:bg-(--color-surface-muted) transition-colors"
        >
          <span>{t`Permission Matrix`}</span>
          {matrixExpanded ? (
            <ChevronUp className="h-4 w-4 text-(--color-text-secondary)" />
          ) : (
            <ChevronDown className="h-4 w-4 text-(--color-text-secondary)" />
          )}
        </button>
        {matrixExpanded && (
          <PermissionMatrix
            roles={allRolesForMatrix}
            permissionGroups={permissionGroups}
          />
        )}
      </div>

      {/* Scope assignments */}
      <div className="rounded-lg border">
        <button
          type="button"
          onClick={() => setScopesExpanded((v) => !v)}
          className="w-full flex items-center justify-between px-4 py-3 bg-(--color-surface-muted) border-b text-sm font-medium hover:bg-(--color-surface-muted) transition-colors"
        >
          <span>{t`Scope Assignments`}</span>
          {scopesExpanded ? (
            <ChevronUp className="h-4 w-4 text-(--color-text-secondary)" />
          ) : (
            <ChevronDown className="h-4 w-4 text-(--color-text-secondary)" />
          )}
        </button>
        {scopesExpanded && (
          <ScopeAssignmentsTable
            roles={[...customRoles, ...systemRoles]}
            assignments={scopeAssignments}
            onRoleClick={(roleId) => {
              const custom = customRoles.find((r) => Number(r.id) === roleId);
              if (custom) {
                setSelectedRole(custom);
                return;
              }
              const system = systemRoles.find((r) => Number(r.id) === roleId);
              if (system) setSelectedSystemRole(system);
            }}
          />
        )}
      </div>

      <RoleSheet
        role={selectedRole}
        onClose={() => setSelectedRole(null)}
        permissionGroups={permissionGroups}
        scopeAssignments={scopeAssignments}
        api={orgRoleApi}
        queryKeyBase="org-roles"
        onRoleDeleted={() => {
          setSelectedRole(null);
          queryClient.invalidateQueries({ queryKey: ["org-roles"] });
        }}
      />

      <RoleSheet
        role={selectedSystemRole}
        onClose={() => setSelectedSystemRole(null)}
        permissionGroups={permissionGroups}
        scopeAssignments={scopeAssignments}
        api={orgRoleApi}
        queryKeyBase="org-roles"
        readOnly
        onRoleDeleted={() => setSelectedSystemRole(null)}
      />
    </div>
  );
}

function OrgRolesSkeleton() {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Skeleton className="h-9 w-32" />
        <Skeleton className="h-9 w-28" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
      <div className="rounded-lg border">
        <div className="p-4 space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      </div>
    </div>
  );
}
